// Credit: https://github.com/javisperez/tailwindcolorshades/blob/master/src/composables/colors.ts

import convert from 'color-convert';

const CMY_HUES = [180, 300, 60];
const RGB_HUES = [360, 240, 120, 0];
// Tailwind also uses 50 with the 0.95 intensity and 950 with the 0.29 intensity.
const INTENSITY_MAP = {
  100: 0.9,
  200: 0.75,
  300: 0.6,
  400: 0.3,
  600: 0.9,
  700: 0.75,
  800: 0.6,
  900: 0.45,
};

type NewTheme = {
  primary?: string;
  red?: string;
  yellow?: string;
  green?: string;
  blue?: string;
  gray?: string;
};

function hueShift(hues: Array<number>, hue: number, intensity: number) {
  const closestHue = hues.sort((a, b) => Math.abs(a - hue) - Math.abs(b - hue))[0];
  const hueShift = closestHue - hue;
  return Math.round(intensity * hueShift * 0.5);
}

function lighten(hex: string, intensity: number): string {
  if (!hex) return '';

  const [h, s, v] = convert.hex.hsv(hex);
  const hue = h + hueShift(CMY_HUES, h, intensity);
  const saturation = s - Math.round(s * intensity);
  const value = v + Math.round((100 - v) * intensity);

  return `#${convert.hsv.hex([hue, saturation, value])}`;
}

function darken(hex: string, intensity: number): string {
  if (!hex) return '';

  const inverseIntensity = 1 - intensity;
  const [h, s, v] = convert.hex.hsv(hex);
  const hue = h + hueShift(RGB_HUES, h, inverseIntensity);
  const saturation = s + Math.round((100 - s) * inverseIntensity);
  const value = v - Math.round(v * inverseIntensity);

  return `#${convert.hsv.hex([hue, saturation, value])}`;
}

function sixDigitsColorHex(hexColor: string) {
  const hexValue = hexColor.replace('#', '');
  return `#${hexValue.length === 3 ? hexValue.replace(/(.)/g, '$1$1') : hexValue.padEnd(6, '0')}`;
}
function getTextOverPrimary(hexColor: string) {
  const [r, g, b] = convert.hex.rgb(hexColor);

  // Credit: https://gomakethings.com/dynamically-changing-the-text-color-based-on-background-color-contrast-with-vanilla-js/
  return (r * 299 + g * 587 + b * 114) / 1000 >= 128 ? 'black' : 'white';
}

function changeColor(name: keyof NewTheme, color: string) {
  const fullColorCode = sixDigitsColorHex(color);

  document.documentElement.style.setProperty(`--h2o-${name}500`, color);

  Object.entries(INTENSITY_MAP).forEach(([level, intensity]) => {
    const color = +level > 500 ? darken(fullColorCode, intensity) : lighten(fullColorCode, intensity);
    document.documentElement.style.setProperty(`--h2o-${name}${level}`, color);
  });

  if (name === 'primary') {
    document.documentElement.style.setProperty('--h2o-textOverPrimary', getTextOverPrimary(color));
  }
}

export const changeTheme = (newTheme: NewTheme) => {
  (Object.entries(newTheme) as [keyof NewTheme, string][]).forEach(([name, color]) => changeColor(name, color));
};

export const cssVarValue = (color: string) => {
  if (!color.startsWith('var(--')) return color;
  color = color.split(',')[0].replace('var(', '');
  return getComputedStyle(document.body).getPropertyValue(color).trim();
};
